import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, Target, RotateCcw, Check } from "lucide-react";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/constants";
import { utilTone, utilBar } from "@/components/debts/CreditUtilization";
import { toast } from "sonner";

const pct = (bal, lim) => (lim > 0 ? Math.round((bal / lim) * 1000) / 10 : 0);

export default function UtilizationSimulator({ credit, plan, onPlanChanged }) {
  const [pay, setPay] = useState({});
  const [goal, setGoal] = useState(plan?.utilization_goal_pct ?? 30);
  const [saving, setSaving] = useState(false);
  const cards = useMemo(() => credit?.cards || [], [credit]);

  useEffect(() => { setPay(Object.fromEntries(cards.map(c => [c.id, 0]))); }, [cards]);
  useEffect(() => { if (plan?.utilization_goal_pct != null) setGoal(plan.utilization_goal_pct); }, [plan]);
  if (!cards.length) return null;

  const rows = cards.map(c => {
    const p = Math.min(parseFloat(pay[c.id]) || 0, c.balance);
    const after = c.balance - p;
    return { ...c, pay: p, after, utilAfter: pct(after, c.credit_limit), need: Math.max(c.balance - c.credit_limit * goal / 100, 0) };
  });
  const totalPay = rows.reduce((s, r) => s + r.pay, 0);
  const totalLimit = credit.total_limit;
  const before = credit.total_utilization;
  const after = pct(rows.reduce((s, r) => s + r.after, 0), totalLimit);
  const needAll = rows.reduce((s, r) => s + r.need, 0);

  // Cheapest way to hit the overall goal: pay down highest-utilization cards first
  const reachOverall = () => {
    let target = Math.max(rows.reduce((s, r) => s + r.balance, 0) - totalLimit * goal / 100, 0);
    const next = {};
    [...rows].sort((a, b) => b.utilization - a.utilization).forEach(r => {
      const p = Math.min(target, r.balance); next[r.id] = Math.round(p * 100) / 100; target -= p;
    });
    setPay(next);
  };
  const reachEach = () => setPay(Object.fromEntries(rows.map(r => [r.id, Math.round(r.need * 100) / 100])));
  const reset = () => setPay(Object.fromEntries(rows.map(r => [r.id, 0])));

  const applyGoal = async () => {
    setSaving(true);
    try { await api.put("/payoff-plan", { utilization_goal_pct: parseFloat(goal) }); toast.success(`Utilization goal set to ${goal}%`); onPlanChanged?.(); }
    catch { toast.error("Could not save goal"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="utilization-simulator">
      <div className="p-5 border-b border-border flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-semibold flex items-center gap-1.5"><CreditCard className="h-3 w-3" /> Credit utilization lab</div>
          <div className="font-display font-bold text-lg mt-1">What if I paid…</div>
          <div className="text-xs text-muted-foreground">Drag a payment per card and watch utilization move. Balances shown are what each card would report.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Goal</span>
            <input type="number" min={1} max={100} value={goal} onChange={e => setGoal(e.target.value)} data-testid="util-sim-goal-input" className="w-16 px-2 py-1 rounded-md border border-border bg-background font-mono text-right" />
            <span className="text-muted-foreground">%</span>
            {parseFloat(goal) !== plan?.utilization_goal_pct && (
              <button onClick={applyGoal} disabled={saving} data-testid="util-sim-save-goal" className="px-2 py-1 rounded-md bg-indigo-500 text-white text-[11px] font-semibold hover:bg-indigo-600 disabled:opacity-50">Save goal</button>
            )}
          </label>
          <button onClick={reachEach} data-testid="util-sim-reach-each" className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-accent">Every card ≤ {goal}%</button>
          <button onClick={reachOverall} data-testid="util-sim-reach-overall" className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-accent">Overall ≤ {goal}% (cheapest)</button>
          <button onClick={reset} title="Reset" data-testid="util-sim-reset" className="p-1.5 rounded-lg border border-border hover:bg-accent"><RotateCcw className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        <div className="lg:col-span-2 divide-y divide-border/60">
          {rows.map(r => (
            <div key={r.id} className="p-4" data-testid={`util-sim-row-${r.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-[11px] text-muted-foreground">{fmt(r.balance)} of {fmt(r.credit_limit)}{r.next_statement_date && ` · reports ${fmtDate(r.next_statement_date)}`}</div>
                </div>
                <div className="text-right font-mono text-sm">
                  <span className={utilTone(r.utilization)}>{r.utilization}%</span>
                  <span className="text-muted-foreground mx-1.5">→</span>
                  <span className={`font-bold ${utilTone(r.utilAfter)}`} data-testid={`util-sim-after-${r.id}`}>{r.utilAfter}%</span>
                </div>
              </div>
              <div className={`mt-2 h-2 rounded-full overflow-hidden relative ${r.utilAfter <= 0 ? "bg-emerald-500" : "bg-accent"}`} data-testid={`util-sim-bar-${r.id}`}>
                {r.utilAfter > 0 && <div className={`h-full ${utilBar(r.utilization)} opacity-30 absolute inset-y-0 left-0`} style={{ width: `${Math.min(r.utilization, 100)}%` }} />}
                <div className={`h-full ${utilBar(r.utilAfter)} absolute inset-y-0 left-0 transition-[width]`} style={{ width: `${Math.min(r.utilAfter, 100)}%` }} />
                <div className="absolute top-0 bottom-0 w-px bg-foreground/50" style={{ left: `${goal}%` }} />
              </div>
              <div className="mt-2 flex items-center gap-3">
                <input type="range" min={0} max={Math.max(r.balance, 1)} step={5} value={r.pay} disabled={r.balance <= 0}
                  onChange={e => setPay(p => ({ ...p, [r.id]: parseFloat(e.target.value) }))} data-testid={`util-sim-slider-${r.id}`} className="flex-1 accent-rose-500" />
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <input type="number" min={0} max={r.balance} step="0.01" value={r.pay} disabled={r.balance <= 0}
                    onChange={e => setPay(p => ({ ...p, [r.id]: parseFloat(e.target.value) || 0 }))} data-testid={`util-sim-input-${r.id}`}
                    className="w-28 pl-5 pr-2 py-1 rounded-md border border-border bg-background text-sm font-mono text-right" />
                </div>
                {r.need > 0 && (
                  <button onClick={() => setPay(p => ({ ...p, [r.id]: Math.round(r.need * 100) / 100 }))} data-testid={`util-sim-need-${r.id}`}
                    className="text-[11px] text-indigo-400 hover:underline whitespace-nowrap">to {goal}%: {fmt(r.need)}</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 bg-accent/20 border-t lg:border-t-0 lg:border-l border-border">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Overall utilization</div>
          <div className="flex items-end gap-3 mt-1">
            <div className={`font-display font-mono text-2xl font-extrabold ${utilTone(before)}`}>{before}%</div>
            <div className="text-muted-foreground pb-1">→</div>
            <div className={`font-display font-mono text-4xl font-extrabold ${utilTone(after)}`} data-testid="util-sim-overall-after">{after}%</div>
          </div>
          <div className={`mt-3 h-2.5 rounded-full overflow-hidden relative ${after <= 0 ? "bg-emerald-500" : "bg-accent"}`} data-testid="util-sim-overall-bar">
            <div className={`h-full ${utilBar(after)} transition-[width]`} style={{ width: `${Math.min(after, 100)}%` }} />
            <div className="absolute top-0 bottom-0 w-px bg-foreground/50" style={{ left: `${goal}%` }} />
          </div>
          <dl className="mt-4 space-y-2 text-xs">
            <Row k="You'd pay" v={fmt(totalPay)} testid="util-sim-total-pay" strong />
            <Row k={`Every card ≤ ${goal}% costs`} v={fmt(needAll)} />
            <Row k="Available credit after" v={fmt(totalLimit - rows.reduce((s, r) => s + r.after, 0))} />
            <Row k="Cards under goal after" v={`${rows.filter(r => r.utilAfter <= goal).length} / ${rows.length}`} />
          </dl>
          {after <= goal ? (
            <div className="mt-4 text-xs text-emerald-500 font-semibold flex items-center gap-1.5" data-testid="util-sim-goal-met"><Check className="h-4 w-4" /> Overall utilization would be at or under your {goal}% goal</div>
          ) : (
            <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5"><Target className="h-4 w-4 text-indigo-400" /> Still {Math.round((after - goal) * 10) / 10} points above goal</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, testid, strong }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`font-mono ${strong ? "font-bold text-sm" : ""}`} data-testid={testid}>{v}</dd>
    </div>
  );
}
